/**
 * Remappable controls. Bindings map player-facing actions to keyboard codes
 * (`KeyboardEvent.code`) and standard-mapping gamepad button indices. They only
 * change how raw devices turn into `InputFrame`s (src/core/input.ts), so the
 * sim, replays and lockstep never see them.
 *
 * Escape always pauses/backs out and gamepad A/B always confirm/back in menus,
 * whatever the bindings say, so a player can't lock themselves out.
 */

export const BIND_ACTIONS = ['moveUp', 'moveDown', 'moveLeft', 'moveRight', 'ability', 'pause', 'zoomIn', 'zoomOut'] as const;
export type BindAction = (typeof BIND_ACTIONS)[number];

export const ACTION_LABELS: Record<BindAction, string> = {
  moveUp: 'Move up',
  moveDown: 'Move down',
  moveLeft: 'Move left',
  moveRight: 'Move right',
  ability: 'Ability',
  pause: 'Pause',
  zoomIn: 'Zoom in',
  zoomOut: 'Zoom out',
};

/** Keyboard slots per action (primary, secondary). */
export const KEY_SLOTS = 2;

export interface Bindings {
  keyboard: Record<BindAction, string[]>;
  /** Button indices; the left stick always moves as well. */
  gamepad: Record<BindAction, number[]>;
}

export function defaultBindings(): Bindings {
  return {
    keyboard: {
      moveUp: ['KeyW', 'ArrowUp'],
      moveDown: ['KeyS', 'ArrowDown'],
      moveLeft: ['KeyA', 'ArrowLeft'],
      moveRight: ['KeyD', 'ArrowRight'],
      ability: ['Space', 'ShiftLeft'],
      pause: ['KeyP'],
      zoomIn: ['Equal', 'NumpadAdd'],
      zoomOut: ['Minus', 'NumpadSubtract'],
    },
    gamepad: {
      moveUp: [12],
      moveDown: [13],
      moveLeft: [14],
      moveRight: [15],
      ability: [0, 5],
      pause: [9],
      zoomIn: [6],
      zoomOut: [7],
    },
  };
}

/** Keys that can never be bound: Escape is the permanent pause/back, F3/Backquote the debug overlay. */
export const RESERVED_KEYS = new Set(['Escape', 'F3', 'Backquote']);
/** Gamepad menu confirm/back stay A/B; binding them to gameplay is fine, but they still work in menus. */
export const MAX_GAMEPAD_BUTTON = 16;

/** Clean untrusted (saved/cloud) bindings: unknown actions dropped, bad values removed, missing actions defaulted. */
export function sanitizeBindings(raw: unknown): Bindings {
  const def = defaultBindings();
  const src = raw && typeof raw === 'object' ? (raw as Partial<Record<keyof Bindings, unknown>>) : {};
  const kb = src.keyboard && typeof src.keyboard === 'object' ? (src.keyboard as Record<string, unknown>) : {};
  const gp = src.gamepad && typeof src.gamepad === 'object' ? (src.gamepad as Record<string, unknown>) : {};
  const out = defaultBindings();
  for (const action of BIND_ACTIONS) {
    const keys = kb[action];
    if (Array.isArray(keys)) {
      out.keyboard[action] = [...new Set(keys.filter((k): k is string => typeof k === 'string' && /^[A-Za-z0-9]{1,24}$/.test(k) && !RESERVED_KEYS.has(k)))].slice(0, KEY_SLOTS);
    } else out.keyboard[action] = def.keyboard[action];
    const buttons = gp[action];
    if (Array.isArray(buttons)) {
      out.gamepad[action] = [...new Set(buttons.filter((b): b is number => Number.isInteger(b) && b >= 0 && b <= MAX_GAMEPAD_BUTTON))].slice(0, 2);
    } else out.gamepad[action] = def.gamepad[action];
  }
  return out;
}

export interface BindResult {
  bindings: Bindings;
  /** The action that lost this input, if it was already bound elsewhere. */
  movedFrom: BindAction | null;
}

/**
 * Bind a key to `action` in keyboard `slot`. If another action already uses the
 * key, it swaps: that action gets whatever `action` had in the slot.
 */
export function bindKey(b: Bindings, action: BindAction, slot: number, code: string): BindResult {
  const next = cloneBindings(b);
  const own = next.keyboard[action].filter((k) => k !== code);
  const at = Math.min(slot, own.length);
  const previous = own[at] as string | undefined;
  let movedFrom: BindAction | null = null;
  for (const other of BIND_ACTIONS) {
    if (other === action) continue;
    const i = next.keyboard[other].indexOf(code);
    if (i < 0) continue;
    movedFrom = other;
    if (previous && !next.keyboard[other].includes(previous)) next.keyboard[other][i] = previous;
    else next.keyboard[other].splice(i, 1);
  }
  own[at] = code;
  next.keyboard[action] = own.slice(0, KEY_SLOTS);
  return { bindings: next, movedFrom };
}

/** Bind a gamepad button as `action`'s only button, swapping with any action that had it. */
export function bindButton(b: Bindings, action: BindAction, button: number): BindResult {
  const next = cloneBindings(b);
  const previous = next.gamepad[action];
  let movedFrom: BindAction | null = null;
  for (const other of BIND_ACTIONS) {
    if (other === action || !next.gamepad[other].includes(button)) continue;
    movedFrom = other;
    const rest = next.gamepad[other].filter((x) => x !== button);
    next.gamepad[other] = rest.length ? rest : previous.filter((x) => x !== button).slice(0, 1);
  }
  next.gamepad[action] = [button];
  return { bindings: next, movedFrom };
}

export function clearKey(b: Bindings, action: BindAction, slot: number): Bindings {
  const next = cloneBindings(b);
  next.keyboard[action] = next.keyboard[action].filter((_, i) => i !== slot);
  return next;
}

export function cloneBindings(b: Bindings): Bindings {
  const out = defaultBindings();
  for (const action of BIND_ACTIONS) {
    out.keyboard[action] = [...b.keyboard[action]];
    out.gamepad[action] = [...b.gamepad[action]];
  }
  return out;
}

/** Reverse lookup for keydown handling. */
export function actionsForKey(b: Bindings, code: string): BindAction[] {
  return BIND_ACTIONS.filter((a) => b.keyboard[a].includes(code));
}

/* ------------------------------------------------------------------ labels */

const KEY_NAMES: Record<string, string> = {
  Space: 'Space', ShiftLeft: 'L-Shift', ShiftRight: 'R-Shift', ControlLeft: 'L-Ctrl', ControlRight: 'R-Ctrl',
  AltLeft: 'L-Alt', AltRight: 'R-Alt', MetaLeft: 'L-Meta', MetaRight: 'R-Meta', Enter: 'Enter', Tab: 'Tab',
  Backspace: 'Backspace', CapsLock: 'Caps', ArrowUp: '↑', ArrowDown: '↓', ArrowLeft: '←', ArrowRight: '→',
  Equal: '=', Minus: '-', BracketLeft: '[', BracketRight: ']', Semicolon: ';', Quote: "'", Comma: ',',
  Period: '.', Slash: '/', Backslash: '\\', NumpadAdd: 'Num +', NumpadSubtract: 'Num -', NumpadEnter: 'Num Enter',
  Delete: 'Del', Insert: 'Ins', Home: 'Home', End: 'End', PageUp: 'PgUp', PageDown: 'PgDn',
};

export function keyLabel(code: string): string {
  if (KEY_NAMES[code]) return KEY_NAMES[code];
  if (/^Key[A-Z]$/.test(code)) return code.slice(3);
  if (/^Digit\d$/.test(code)) return code.slice(5);
  if (/^Numpad\d$/.test(code)) return `Num ${code.slice(6)}`;
  return code;
}

export type PadFamily = 'playstation' | 'xbox' | 'generic';

const XBOX = ['A', 'B', 'X', 'Y', 'LB', 'RB', 'LT', 'RT', 'View', 'Menu', 'LS', 'RS', 'D↑', 'D↓', 'D←', 'D→', 'Xbox'];
const PLAYSTATION = ['✕', '○', '□', '△', 'L1', 'R1', 'L2', 'R2', 'Create', 'Options', 'L3', 'R3', 'D↑', 'D↓', 'D←', 'D→', 'PS'];

export function buttonLabel(button: number, family: PadFamily): string {
  if (family === 'playstation') return PLAYSTATION[button] ?? `B${button}`;
  if (family === 'xbox') return XBOX[button] ?? `B${button}`;
  // Generic pads: Xbox-style names are the most widely understood fallback.
  return XBOX[button] ?? `B${button}`;
}

/** Typing into a text field must never be hijacked by menu navigation or game keys. */
export function isEditableTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el || typeof el.tagName !== 'string') return false;
  if (el.isContentEditable) return true;
  if (el.tagName === 'TEXTAREA' || el.tagName === 'SELECT') return true;
  if (el.tagName !== 'INPUT') return false;
  const type = (el as HTMLInputElement).type;
  return !['range', 'checkbox', 'radio', 'button', 'submit'].includes(type);
}
